/* eslint-disable @typescript-eslint/no-explicit-any */
import isEmpty from 'lodash/isEmpty';
import pick from 'lodash/pick';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Header, Item } from 'semantic-ui-react';

import BlockContainer from '../../../components/BlockContainer';
import EntityLink from '../../../components/EntityLink';
import { ContainerProps } from '../../../components/cms/types';
import { bem } from '../../../core/design/bem';
import { matchesWithArray } from '../../../core/filter/utils';
import { Workshop, WorkshopSession } from '../../../types/workshop.types';
import Images from '../../../utils/Images';
import { useSyncedCollectionWorkshopSessions } from '../../../workshop-session/store/workshopSessions.hooks';
import './SuggestedWorkshops.scss';

const translationPrefix = 'workshops.workshop.suggested-sessions';
const css = bem('SuggestedWorkshops');

type Props = {
  _id: string;
  container: ContainerProps;
  item: Workshop;
  fieldsToMatch?: Record<string, any>;
};

function useSuggestedSessions(workshop: Workshop, fieldsToMatch?: Record<string, any>) {
  const sessions = useSyncedCollectionWorkshopSessions(workshop.collection) as WorkshopSession[];
  return useMemo(() => {
    if (isEmpty(fieldsToMatch)) return [];
    const matchObject = pick(workshop, fieldsToMatch);
    const otherSessions = sessions.filter((s) => s.workshopId !== workshop._id);
    return otherSessions.filter(matchesWithArray(matchObject));
  }, [workshop, fieldsToMatch, sessions]);
}

export const SuggestedWorkshops = (props: Props): JSX.Element | null => {
  const { t } = useTranslation();
  const { item, container, fieldsToMatch } = props;
  const sessions = useSuggestedSessions(item, fieldsToMatch);
  if (sessions.length === 0) return null;

  return (
    <BlockContainer className={css()} {...container}>
      <Header as="h2">{t(`${translationPrefix}.header`)}</Header>
      <Item.Group unstackable>
        {sessions.map((session) => {
          const { title, _id, image, workshopId, startDate, endDate } = session;
          return (
            <Item
              className={css('Item').toString()}
              key={_id}
              as={EntityLink}
              entity={{ ...session, _id: workshopId, kind: 'workshops' }}
            >
              {image && image.uri && (
                <Item.Image
                  size="small"
                  src={Images.cdn(image)}
                  style={{ maxWidth: 100, marginRight: 10 }}
                />
              )}
              <Item.Content>
                <Item.Header className="title" as="h3">
                  {title}
                </Item.Header>
                <Item.Description className="date">
                  {t(`${translationPrefix}.date`, { startDate, endDate })}
                </Item.Description>
              </Item.Content>
            </Item>
          );
        })}
      </Item.Group>
    </BlockContainer>
  );
};

SuggestedWorkshops.defaultProps = {
  fieldsToMatch: {},
};
